import { createRoot, Root } from "react-dom/client";
import React from "react";
import { jssPreset, StylesProvider } from "@mui/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { create } from "jss";
import {
    GLOBAL_STYLES,
    LIGHT_BOX_STYLES,
    SWIPER_GLOBAL_STYLES,
} from "./styles";
import { PostDetailsModal } from "./Presentation/Components/Modals/PostDetailsModal/PostDetailsModal";
import { PostDetailsEntrypoint } from "./Presentation/Components/PostDetailsEntrypoint/PostDetailsEntrypoint";
import { PostListEntryComponent } from "./Presentation/Components/EntryFeedModal/PostListEntryComponent";
import { PostsListFeedEntryComponent } from "./Presentation/Pages/PostsFeed/PostsListFeedEntryComponent";
import { GeneralErrorsModal } from "./Presentation/Components/Modals/ServiceModals/GeneralErrorsModal/GeneralErrorsModal.component";
import { TimelinePage } from "./Presentation/Pages/TimelinePage/TimelinePage";
import { PostsPromoEntryComponent } from "./Presentation/Pages/PostsPromoFeed/PostsPromoEntryComponent";
import { PostsPromoListArea } from "./Presentation/Pages/PostsPromoFeed/PostsPromoListArea";

class ExternalFeedComponent extends HTMLElement {
    private mountPoint: HTMLDivElement | undefined = undefined;
    private root: Root | undefined = undefined;

    constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
        if (this.shadowRoot === null) {
            throw new Error("Shadow root is not available");
        }
        const mountPoint = document.createElement("div");
        mountPoint.classList.add("community__root");
        this.shadowRoot.appendChild(mountPoint);
        const emotionRoot = document.createElement("style");
        this.shadowRoot.appendChild(emotionRoot);

        const swiperLibraryStyles = document.createElement("style");
        swiperLibraryStyles.innerHTML = SWIPER_GLOBAL_STYLES;
        this.shadowRoot.appendChild(swiperLibraryStyles);

        const generalStyles = document.createElement("style");
        generalStyles.innerHTML = GLOBAL_STYLES;
        this.shadowRoot.appendChild(generalStyles);

        const lightboxStyles = document.createElement("style");
        lightboxStyles.innerHTML = LIGHT_BOX_STYLES;
        document.getElementsByTagName("head")[0]?.appendChild(lightboxStyles);

        const jss = create({
            ...jssPreset(),
            insertionPoint: mountPoint,
        });
        const cache = createCache({
            key: "css",
            prepend: true,
            container: emotionRoot,
        });
        const isDashboardFeed = this.getAttribute("isDashboardFeed") == "1";
        const userScreenName = this.getAttribute("screenName") ?? undefined;
        const userAvatar = this.getAttribute("profilePicture") ?? undefined;

        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);

        const root = createRoot(mountPoint);
        root.render(
            <StylesProvider jss={jss}>
                <CacheProvider value={cache}>
                    <PostsListFeedEntryComponent
                        isDashboardFeed={isDashboardFeed}
                        userScreenName={userScreenName}
                        userAvatar={userAvatar}
                        params={params}
                    />
                    <GeneralErrorsModal />
                </CacheProvider>
            </StylesProvider>,
        );
        this.mountPoint = mountPoint;
        this.root = root;
    }

    disconnectedCallback() {
        if (this.mountPoint) {
            this.root?.unmount();
            this.shadowRoot?.removeChild(this.mountPoint);
            this.mountPoint = undefined;
        }
    }
}

class ExternalPostModalComponent extends HTMLElement {
    private mountPoint: HTMLDivElement | undefined = undefined;
    private root: Root | undefined = undefined;

    constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
        if (this.shadowRoot === null) {
            throw new Error("Shadow root is not available");
        }
        const mountPoint = document.createElement("div");
        mountPoint.classList.add("community__root");
        this.shadowRoot.appendChild(mountPoint);

        const emotionRoot = document.createElement("style");
        this.shadowRoot.appendChild(emotionRoot);

        const swiperLibraryStyles = document.createElement("style");
        swiperLibraryStyles.innerHTML = SWIPER_GLOBAL_STYLES;
        this.shadowRoot.appendChild(swiperLibraryStyles);

        const generalStyles = document.createElement("style");
        generalStyles.innerHTML = GLOBAL_STYLES;
        this.shadowRoot.appendChild(generalStyles);

        const lightboxStyles = document.createElement("style");
        lightboxStyles.innerHTML = LIGHT_BOX_STYLES;
        document.getElementsByTagName("head")[0]?.appendChild(lightboxStyles);

        const jss = create({
            ...jssPreset(),
            insertionPoint: mountPoint,
        });

        const cache = createCache({
            key: "css",
            prepend: true,
            container: emotionRoot,
        });

        const url = new URL(window.location.href);

        const params = new URLSearchParams(url.search);

        const postId = params.get("postId") ?? "";
        const action = params.get("action") ?? "";
        const commentId = params.get("commentId") ?? "";

        const root = createRoot(mountPoint);
        root.render(
            <StylesProvider jss={jss}>
                <CacheProvider value={cache}>
                    {postId && (
                        <>
                            <PostDetailsEntrypoint
                                postId={postId}
                                action={action}
                                commentId={commentId}
                            />
                            <PostDetailsModal fullScreen={true} />
                        </>
                    )}
                    <GeneralErrorsModal />
                </CacheProvider>
            </StylesProvider>,
        );
        this.mountPoint = mountPoint;
        this.root = root;
    }

    disconnectedCallback() {
        if (this.mountPoint) {
            this.root?.unmount();
            this.shadowRoot?.removeChild(this.mountPoint);
            this.mountPoint = undefined;
        }
    }
}

class ExternalFeedEntryComponent extends HTMLElement {
    private mountPoint: HTMLDivElement | undefined = undefined;
    private root: Root | undefined = undefined;

    constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
        if (this.shadowRoot === null) {
            throw new Error("Shadow root is not available");
        }
        const mountPoint = document.createElement("div");
        mountPoint.classList.add("community__root");
        this.shadowRoot.appendChild(mountPoint);

        const emotionRoot = document.createElement("style");
        this.shadowRoot.appendChild(emotionRoot);

        const swiperLibraryStyles = document.createElement("style");
        swiperLibraryStyles.innerHTML = SWIPER_GLOBAL_STYLES;
        this.shadowRoot.appendChild(swiperLibraryStyles);

        const generalStyles = document.createElement("style");
        generalStyles.innerHTML = GLOBAL_STYLES;
        this.shadowRoot.appendChild(generalStyles);

        const lightboxStyles = document.createElement("style");
        lightboxStyles.innerHTML = LIGHT_BOX_STYLES;
        document.getElementsByTagName("head")[0]?.appendChild(lightboxStyles);

        const jss = create({
            ...jssPreset(),
            insertionPoint: mountPoint,
        });

        const cache = createCache({
            key: "css",
            prepend: true,
            container: emotionRoot,
        });

        const root = createRoot(mountPoint);
        root.render(
            <StylesProvider jss={jss}>
                <CacheProvider value={cache}>
                    <PostListEntryComponent />
                    <GeneralErrorsModal />
                </CacheProvider>
            </StylesProvider>,
        );
        this.mountPoint = mountPoint;
        this.root = root;
    }

    disconnectedCallback() {
        if (this.mountPoint) {
            this.root?.unmount();
            this.shadowRoot?.removeChild(this.mountPoint);
            this.mountPoint = undefined;
        }
    }
}

class ExternalTimelineComponent extends HTMLElement {
    private mountPoint: HTMLDivElement | undefined = undefined;
    private root: Root | undefined = undefined;

    constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
        if (this.shadowRoot === null) {
            throw new Error("Shadow root is not available");
        }
        const mountPoint = document.createElement("div");
        mountPoint.classList.add("community__root");
        this.shadowRoot.appendChild(mountPoint);

        const emotionRoot = document.createElement("style");
        this.shadowRoot.appendChild(emotionRoot);

        const swiperLibraryStyles = document.createElement("style");
        swiperLibraryStyles.innerHTML = SWIPER_GLOBAL_STYLES;
        this.shadowRoot.appendChild(swiperLibraryStyles);

        const generalStyles = document.createElement("style");
        generalStyles.innerHTML = GLOBAL_STYLES;
        this.shadowRoot.appendChild(generalStyles);

        const lightboxStyles = document.createElement("style");
        lightboxStyles.innerHTML = LIGHT_BOX_STYLES;
        document.getElementsByTagName("head")[0]?.appendChild(lightboxStyles);

        const jss = create({
            ...jssPreset(),
            insertionPoint: mountPoint,
        });

        const cache = createCache({
            key: "css",
            prepend: true,
            container: emotionRoot,
        });

        const userScreenName = this.getAttribute("screenName") ?? undefined;
        const userAvatar = this.getAttribute("profilePicture") ?? undefined;

        const root = createRoot(mountPoint);
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        const gameId = params.get("gameId") ?? "";

        root.render(
            <StylesProvider jss={jss}>
                <CacheProvider value={cache}>
                    <TimelinePage
                        gameId={gameId}
                        userScreenName={userScreenName}
                        userAvatar={userAvatar}
                        params={params}
                    />
                    <GeneralErrorsModal />
                </CacheProvider>
            </StylesProvider>,
        );
        this.mountPoint = mountPoint;
        this.root = root;
    }

    disconnectedCallback() {
        if (this.mountPoint) {
            this.root?.unmount();
            this.shadowRoot?.removeChild(this.mountPoint);
            this.mountPoint = undefined;
        }
    }
}

/** Posts marked as promo in backoffice shown separately */
class ExternalFeedPromoComponent extends HTMLElement {
    private mountPoint: HTMLDivElement | undefined = undefined;
    private root: Root | undefined = undefined;

    constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
        if (this.shadowRoot === null) {
            throw new Error("Shadow root is not available");
        }
        const mountPoint = document.createElement("div");
        mountPoint.classList.add("community__root");
        this.shadowRoot.appendChild(mountPoint);
        const emotionRoot = document.createElement("style");
        this.shadowRoot.appendChild(emotionRoot);

        const swiperLibraryStyles = document.createElement("style");
        swiperLibraryStyles.innerHTML = SWIPER_GLOBAL_STYLES;
        this.shadowRoot.appendChild(swiperLibraryStyles);

        const generalStyles = document.createElement("style");
        generalStyles.innerHTML = GLOBAL_STYLES;
        this.shadowRoot.appendChild(generalStyles);

        const lightboxStyles = document.createElement("style");
        lightboxStyles.innerHTML = LIGHT_BOX_STYLES;
        document.getElementsByTagName("head")[0]?.appendChild(lightboxStyles);

        const jss = create({
            ...jssPreset(),
            insertionPoint: mountPoint,
        });
        const cache = createCache({
            key: "css",
            prepend: true,
            container: emotionRoot,
        });

        const area = this.getAttribute("area") ?? "above-bets";

        const root = createRoot(mountPoint);
        root.render(
            <StylesProvider jss={jss}>
                <CacheProvider value={cache}>
                    <PostsPromoListArea area={area}>
                        <PostsPromoEntryComponent />
                    </PostsPromoListArea>
                    <GeneralErrorsModal />
                </CacheProvider>
            </StylesProvider>,
        );
        this.mountPoint = mountPoint;
        this.root = root;
    }

    disconnectedCallback() {
        if (this.mountPoint) {
            this.root?.unmount();
            this.shadowRoot?.removeChild(this.mountPoint);
            this.mountPoint = undefined;
        }
    }
}

!customElements.get("feed-component") &&
    customElements.define("feed-component", ExternalFeedComponent);

!customElements.get("feed-promo-component") &&
    customElements.define("feed-promo-component", ExternalFeedPromoComponent);

!customElements.get("community-post-modal") &&
    customElements.define("community-post-modal", ExternalPostModalComponent);

!customElements.get("community-feed-modal") &&
    customElements.define("community-feed-modal", ExternalFeedEntryComponent);

!customElements.get("community-game-timeline") &&
    customElements.define("community-game-timeline", ExternalTimelineComponent);
